import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'

export type CommsView = 'open' | 'snoozed' | 'done'
export type CommsStatus = 'new' | 'acknowledged' | 'replied' | 'done' | 'snoozed'

export interface CommsItem {
  id: string
  channel: 'slack'
  conversationId: string | null
  conversationName: string | null
  conversationKind: 'public' | 'private' | 'group_dm' | null
  fromName: string | null
  body: string | null
  permalink: string | null
  status: CommsStatus
  snoozedUntil: string | null
  happenedAt: string
}

export const commsKeys = {
  all: ['comms'] as const,
  items: (view: CommsView) => ['comms', 'items', view] as const,
  summary: ['comms', 'summary'] as const,
}

export const useCommsItems = (view: CommsView) =>
  useQuery({ queryKey: commsKeys.items(view), queryFn: () => apiGet<{ items: CommsItem[] }>('comms/items', { view }), refetchInterval: 30_000, refetchOnWindowFocus: true, staleTime: 10_000 })

/** What the sidebar badge shows. Cheap and polled, like the bell. */
export const useCommsSummary = (enabled = true) =>
  useQuery({ queryKey: commsKeys.summary, queryFn: () => apiGet<{ open: number; unseen: number }>('comms/summary'), refetchInterval: 60_000, refetchOnWindowFocus: true, staleTime: 30_000, enabled })

export function useSetCommsStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; status: CommsStatus; snoozeUntil?: string }) => apiPost<CommsItem>(`comms/items/${v.id}/status`, { status: v.status, ...(v.snoozeUntil ? { snoozeUntil: v.snoozeUntil } : {}) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: commsKeys.all }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** The Tapwork bell's notifications, listed in full beside the mentions. */
export interface TapworkNotification {
  id: string
  kind: string
  title: string
  body: string | null
  href: string | null
  readAt: string | null
  createdAt: string
}
export const useTapworkNotifications = () =>
  useQuery({ queryKey: ['comms', 'notifications'] as const, queryFn: () => apiGet<{ items: TapworkNotification[]; unread: number }>('notify/inbox', { limit: 100 }), refetchInterval: 30_000, staleTime: 10_000 })

export { snoozePresets } from './snooze'
